import React, { Component } from 'react';
import { shape, string, func } from 'prop-types';
import { connect } from 'react-redux';
import { extraSurveyPartPermission, hasPermission, surveySchemaPermission } from 'unihow-common';
import {
  getSurveySchemaPart, getSurveySchemaPartUsage, getExtraSurveyPart, getExtraSurveyPartUsage
} from '../../api';
import { addErrorMessageAction, setTaskPending, setTaskFulfilled } from '../../actions';
import { routes } from '../../constants';
import {
  getCompiledPath, getSurveyPartType, isSurveySchemaPartType, setDocumentTitle
} from '../../util';
import TabsContainer from '../TabsContainer';
import Questions from './Questions';
import Dimensions from './Dimensions';
import TabPane from '../TabPane';
import InfoBox from './InfoBox';
import styles from './surveyPartView.module.css';
import ActionBar from './ActionBar';
import { publicationStatusValues, surveyPartTypeValues } from '../../constants/types';
import { withLang, langType } from '../../i18n';
import PreviewLanguageSelector from '../Preview/PreviewLanguageSelector';
import withRouter from '../WithRouter';

class SurveyPartView extends Component {
  constructor(props) {
    super(props);

    this.state = { previewLang: props.lang };

    this.onLanguageSelect = this.onLanguageSelect.bind(this);
    this.getSurveyPartData = this.getSurveyPartData.bind(this);
  }

  componentDidMount() {
    this.getSurveyPartData();
  }

  onLanguageSelect(previewLang) {
    this.setState({ previewLang });
  }

  getSurveyPartData() {
    this.props.dispatchSetTaskPending();
    const { surveyPartId } = this.props.params;
    const surveyPartType = getSurveyPartType(this.props.location.pathname);
    let getSurveyPart;
    let getSurveyPartUsage;
    if (surveyPartType === surveyPartTypeValues.SURVEY_SCHEMA_PART) {
      getSurveyPart = getSurveySchemaPart;
      getSurveyPartUsage = getSurveySchemaPartUsage;
    } else {
      getSurveyPart = getExtraSurveyPart;
      getSurveyPartUsage = getExtraSurveyPartUsage;
    }

    Promise.all(
      [
        getSurveyPart(surveyPartId),
        getSurveyPartUsage(surveyPartId)
      ]
    ).then(([surveyPart, surveyPartUsage]) => {
      this.setState({ surveyPart, surveyPartUsage });
    })
      .catch((err) => {
        if (err.status !== 404) {
          this.props.dispatchAddErrorMessage(err);
        }
      })
      .finally(this.props.dispatchSetTaskFulfilled);
  }

  renderSurveyPartComponents() {
    const { translate: t } = this.props;
    const { surveyPart, previewLang } = this.state;
    if (isSurveySchemaPartType(surveyPart.type)) {
      return (
        <TabsContainer>
          <TabPane label={t('surveyParts.questions')}>
            <Questions surveyPart={surveyPart} translate={t} lang={previewLang} />
          </TabPane>
          <TabPane label={t('surveySchemaParts.dimensions.dimensions')}>
            <Dimensions surveyPart={surveyPart} translate={t} lang={previewLang} updatedCallback={this.getSurveyPartData} />
          </TabPane>
        </TabsContainer>
      );
    }

    return <Questions surveyPart={surveyPart} translate={t} lang={previewLang} />;
  }

  render() {
    const { surveyPart, surveyPartUsage, previewLang } = this.state;
    const { translate: t, navigate } = this.props;
    const { surveyPartId } = this.props.params;
    const navigateTo = (route) => () =>
      navigate(getCompiledPath(route, { surveyPartId }));

    if (!surveyPart) {
      return null;
    }
    const extraPart = surveyPart.type === surveyPartTypeValues.EXTRA_SURVEY_PART;
    const routeBase = extraPart ? routes.surveySchemas.extraSurveyParts.surveyPart : routes.surveySchemas.surveyParts.surveyPart;
    const translationBase = extraPart ? 'extraSurveyParts' : 'surveySchemaParts';
    const hasEditPermission = extraPart
      ? hasPermission(this.props.userPerms, extraSurveyPartPermission(true))
      : hasPermission(this.props.userPerms, surveySchemaPermission(true));

    setDocumentTitle(t(`${translationBase}.surveyPart`));

    return (
      <div className="page-container">
        <div className={styles.titleRow}>
          <h1 className={`page-title--small ${styles.title}`}>
            {t(`${translationBase}.surveyPart`)}
          </h1>
          {hasEditPermission
            && (
              <ActionBar
                onEdit={navigateTo(routeBase.edit.index)}
                onNewVersion={(!extraPart && publicationStatusValues.PUBLISHED === surveyPart.status
                  && navigateTo(routeBase.newVersion.index)) || undefined}
                onCopy={navigateTo(routeBase.copy.index)}
                versionable={!extraPart}
              />
            )}
        </div>
        <PreviewLanguageSelector
          previewLang={previewLang}
          onLanguageSelect={this.onLanguageSelect}
        />
        <InfoBox
          surveyPart={surveyPart}
          surveyPartUsage={surveyPartUsage}
        />
        {this.renderSurveyPartComponents()}
      </div>
    );
  }
}

SurveyPartView.propTypes = {
  params: shape({
    surveyPartId: string
  }).isRequired,
  dispatchAddErrorMessage: func.isRequired,
  dispatchSetTaskPending: func.isRequired,
  dispatchSetTaskFulfilled: func.isRequired,
  navigate: func.isRequired,
  ...langType
};

const mapStateToProps = ({ user }) => ({ userPerms: user.currentUser.permissions });

const mapDispatchToProps = (dispatch) => ({
  dispatchAddErrorMessage: (err) => dispatch(addErrorMessageAction(err)),
  dispatchSetTaskPending: () => dispatch(setTaskPending()),
  dispatchSetTaskFulfilled: () => dispatch(setTaskFulfilled())
});

export default withLang(withRouter(connect(mapStateToProps, mapDispatchToProps)(SurveyPartView)));
